"use client";

import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { useStore } from "../../app/stores/store";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { formatDateTime } from "../../lib/utils";

import { Loader2, Lock } from "lucide-react";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Separator } from "../../components/ui/separator";
import { Button } from "../../components/ui/button";

const paymentLogos = [
  { src: "/global/visa.webp", alt: "Visa" },
  { src: "/global/mastercard.webp", alt: "Mastercard" },
  { src: "/global/applepay.webp", alt: "Apple Pay" },
  { src: "/global/paypal.webp", alt: "PayPal" },
];

interface BookingCheckoutProps {
  allFilesUploaded?: boolean;
}

const BookingCheckout = ({ allFilesUploaded }: BookingCheckoutProps) => {
  const navigate = useNavigate();
  const {
    bookingStore,
    paymentStore,
    userStore: { user, isPartner },
  } = useStore();

  const { getBooking, getCurrentSessionBookings } = bookingStore;

  const [bookings, setBookings] = useState<any[]>([]);
  const [sessionLoaded, setSessionLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [paymentType, setPaymentType] = useState("");

  const walletBalance = user?.securityDepositAmount || 0;

  // Load session and bookings
  useEffect(() => {
    const loadSessionAndBookings = async () => {
      const session = getCurrentSessionBookings();
      if (!session || session.length === 0) {
        navigate("/");
        return;
      }

      try {
        const bookingPromises = session.map((id) => getBooking(id));
        const fetched = await Promise.all(bookingPromises);
        const valid = fetched.filter((b) => b !== null);
        setBookings(valid);
      } catch {
        toast.error("Failed to load booking data");
      } finally {
        setSessionLoaded(true);
      }
    };

    loadSessionAndBookings();
  }, [getBooking, getCurrentSessionBookings, navigate]);

  // Early loading state
  if (!sessionLoaded || bookings.length === 0) {
    return (
      <div className="flex items-center justify-center h-[80vh]">
        <Loader2 className="w-10 h-10 animate-spin text-primary" />
      </div>
    );
  }

  const totalPrice = bookings.reduce(
    (sum, x) => sum + (Number(x?.totalPrice) || 0),
    0
  );
  const onlineFee = Number((totalPrice * 0.03).toFixed(2));

  const onConfirmClick = () => {
    if (!paymentType) {
      setError("You must choose a payment method.");
      return;
    }
    bookingStore.removeBookingSession();
    setError("");
    handleConfirmBooking();
  };

  const handleConfirmBooking = async () => {
    const type = { type: paymentType };

    if (paymentType === "Direct" || paymentType === "Wallet") {
      try {
        await Promise.all(
          bookings.map((b) =>
            bookingStore.setPaymentTypeOfBooking(b.id ?? "", type)
          )
        );
        setBookings(bookings.map((b) => ({ ...b, paymentType: "Direct" })));
        toast.success("Booking payment type set to Direct");
      } catch {
        toast.error("Error updating payment type.");
      }
    } else if (paymentType === "Online") {
      setIsLoading(true);
      const formData = new FormData();
      formData.append("Type", "booking");
      formData.append("TransactionAmount", (totalPrice + onlineFee).toFixed(2));
      formData.append("IDS", bookings.map((x) => x.id).join(","));
      formData.append(
        "OrderName",
        bookings.map((x) => x.serviceName).join(",")
      );

      try {
        const result = await paymentStore.initiatePayment(formData);
        if (result?.status === "success") {
          bookings.map((b) =>
            bookingStore.setPaymentTypeOfBooking(b.id ?? "", type)
          );
          window.location.href = result.data as string;
        } else {
          toast.error("Payment initiation failed!");
        }
      } catch {
        toast.error("Error processing online payment.");
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="sticky top-28 gsap-checkout-summary">
      <Card className="border-gray-200  bg-white rounded-xl">
        <CardHeader>
          <CardTitle className="text-2xl">Order Summary</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Services List */}
          <div className="space-y-3">
            {bookings.map((b) => (
              <div key={b.id} className="space-y-2">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium">{b.serviceName}</p>
                    <p className="text-sm text-muted-foreground">
                      {b.duration === "30 days" ? "30 Days" : b.duration}
                    </p>
                  </div>
                  <span className="font-semibold">{b.totalPrice} AED</span>
                </div>

                {b.securityDeposit * (b.adultsNumber + b.childrenNumber) >
                  0 && (
                  <div className="flex justify-between text-sm text-muted-foreground">
                    <span>Security Deposit</span>
                    <span className="text-red-500">
                      {b.securityDeposit * (b.adultsNumber + b.childrenNumber)}{" "}
                      AED
                    </span>
                  </div>
                )}
                <Separator />
              </div>
            ))}
          </div>

          {/* Total Price */}
          <div className="flex justify-between font-semibold text-lg">
            <span>Total Price:</span>
            <span>{totalPrice} AED</span>
          </div>

          {/* Online Payment Fee */}
          {paymentType === "Online" && (
            <>
              <div className="flex justify-between text-muted-foreground">
                <span>Online Payment Fee (3%)</span>
                <span>{onlineFee} AED</span>
              </div>
              <Separator />
              <div className="flex justify-between font-bold text-lg text-primary">
                <span>Total with Fee:</span>
                <span>{(totalPrice + onlineFee).toFixed(2)} AED</span>
              </div>
            </>
          )}

          {/* Wallet Payment Warning */}
          {paymentType === "Wallet" && walletBalance < totalPrice && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">
                ⚠️ Your wallet balance is insufficient. Please top up or use
                another method.
              </p>
            </div>
          )}
        </CardContent>

        <CardFooter className="flex-col items-start gap-4">
          {/* Payment Method Selection */}
          <div className="w-full">
            <h3 className="font-semibold text-lg mb-3">Payment Method</h3>

            {!bookings[0].paymentType ? (
              <div className="space-y-2">
                {/* Cash Payment */}
                <label className="flex items-center justify-between border p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                  <div className="flex items-center gap-2">
                    <i className="fas fa-money-bill-wave text-green-600 text-lg"></i>
                    <span>Cash Payment</span>
                  </div>
                  <input
                    type="radio"
                    value="Direct"
                    checked={paymentType === "Direct"}
                    onChange={(e) => setPaymentType(e.target.value)}
                    disabled={!allFilesUploaded}
                    className="h-4 w-4 text-primary"
                  />
                </label>

                {/* Online Payment */}
                <label className="flex items-center justify-between border p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                  <div className="flex items-center gap-2">
                    <img
                      src="/assets/img/etisalatLogo.gif"
                      alt="Online Payment"
                      width={30}
                      height={30}
                      className="rounded"
                    />
                    <span>Online Payment</span>
                  </div>
                  <input
                    type="radio"
                    value="Online"
                    checked={paymentType === "Online"}
                    onChange={(e) => setPaymentType(e.target.value)}
                    disabled={!allFilesUploaded}
                    className="h-4 w-4 text-primary"
                  />
                </label>

                {/* Wallet Payment */}
                {isPartner() && (
                  <label className="flex items-center justify-between border p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                    <div className="flex items-center gap-2">
                      <i className="fas fa-wallet text-purple-600 text-lg"></i>
                      <span className="text-green-600 font-semibold">
                        Pay from Wallet (
                        {new Intl.NumberFormat().format(Number(walletBalance))}{" "}
                        AED)
                      </span>
                    </div>
                    <input
                      type="radio"
                      value="Wallet"
                      checked={paymentType === "Wallet"}
                      onChange={(e) => setPaymentType(e.target.value)}
                      disabled={!allFilesUploaded}
                      className="h-4 w-4 text-primary"
                    />
                  </label>
                )}

                {/* Upload Required Warning */}
                {!allFilesUploaded && (
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-center gap-2 text-yellow-700">
                      <Lock className="w-4 h-4" />
                      <span className="text-sm">
                        Complete document upload to enable payment options
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-600 text-sm">
                You have already selected:{" "}
                <strong>{bookings[0].paymentType}</strong>
                <br />
                <span className="text-xs">
                  Contact support if you wish to change it.
                </span>
              </p>
            )}
          </div>

          {/* Payment Logos */}
          <div className="w-full">
            <div className="flex items-center gap-2 flex-wrap">
              {paymentLogos.map((logo) => (
                <div
                  key={logo.alt}
                  className="bg-gray-100 border rounded-md p-2 flex items-center justify-center"
                >
                  <img
                    src={logo.src}
                    alt={logo.alt}
                    width={40}
                    height={26}
                    className="object-contain"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          {!bookings[0].paymentType && (
            <div className="w-full space-y-3">
              <Button
                onClick={onConfirmClick}
                disabled={isLoading || !allFilesUploaded}
                className="w-full bg-primary hover:bg-primary/90 text-lg py-6"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : !allFilesUploaded ? (
                  <>
                    <Lock className="w-4 h-4 mr-2" />
                    Upload Required
                  </>
                ) : paymentType === "Online" ? (
                  `Pay AED ${(totalPrice + onlineFee).toFixed(2)}`
                ) : paymentType === "Wallet" ? (
                  `Pay AED ${totalPrice}`
                ) : (
                  `Confirm Booking - AED ${totalPrice}`
                )}
              </Button>

              {/* <Button
                variant="outline"
                onClick={() => navigate("/services")}
                className="w-full"
              >
                Add More Services
              </Button> */}

              <p className="text-xs text-muted-foreground text-center w-full">
                By confirming, you agree to our Terms and Conditions.
              </p>
            </div>
          )}
        </CardFooter>
      </Card>
    </div>
  );
};

export default observer(BookingCheckout);